/**
 * Three screens: auth, lobby, table.
 *
 * The socket is opened once and lives for the whole session, anonymous at
 * first. Which screen shows is decided by whether the connection has an
 * account yet, not by routing — so a session that expires mid-game returns the
 * player to sign-in without unmounting anything else, and a successful sign-in
 * carries straight on to the lobby over the same connection.
 */

import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import type { PlayerAction, ServerHandEvent, ServerMessage } from '@pf/protocol';

import { Actions } from './components/Actions.tsx';
import { Admin } from './components/Admin.tsx';
import { Auth, ResetPassword } from './components/Auth.tsx';
import { Download } from './components/Download.tsx';
import { History } from './components/History.tsx';
import { Landing } from './components/Landing.tsx';
import { Avatar } from './components/avatar.tsx';
import { PageTitle, SiteHeader } from './components/Shell.tsx';
import { Profile } from './components/Profile.tsx';
import { Leaderboard } from './components/Leaderboard.tsx';
import { SngBar, SngList, SngStandings } from './components/Sng.tsx';
import { Table } from './components/Table.tsx';
import { bb } from './fa.ts';
import { FELT_LABEL, feltChoice, nextFelt, setFeltChoice } from './felt.ts';
import { play, setSoundEnabled, soundEnabled } from './sound.ts';
import { initialState, reduce, type ClientState, type LocalAction } from './state.ts';
import { GameSocket, defaultSocketUrl, type ConnectionStatus } from './ws.ts';

const SESSION_KEY = 'pf-session';

/** What an event sounds like. Silence for the ones nobody needs to hear. */
function cue(event: ServerHandEvent): void {
  switch (event.type) {
    case 'hole-cards-dealt':
    case 'street-dealt':
      play('deal');
      break;
    case 'acted':
      if (event.allIn) play('allin');
      else if (event.action === 'fold') play('fold');
      else if (event.action === 'check') play('check');
      else if (event.action === 'call') play('call');
      else if (event.action === 'bet') play('bet');
      else if (event.action === 'raise') play('raise');
      break;
    case 'awarded':
      play('win');
      break;
    default:
      break;
  }
}

/**
 * A link the player arrived on.
 *
 * Read once, at startup, and immediately erased from the address bar. A token
 * in a URL is a token in browser history, in a screenshot, and in the Referer
 * header of anything the page loads next; taking it out of the bar costs one
 * line and removes all three.
 */
type ArrivalLink = { readonly kind: 'verify' | 'reset'; readonly token: string };

function readArrivalLink(): ArrivalLink | null {
  const path = location.pathname.replace(/\/+$/, '');
  const kind = path === '/verify' ? 'verify' : path === '/reset' ? 'reset' : null;
  if (kind === null) return null;
  const token = new URLSearchParams(location.search).get('token');
  history.replaceState(null, '', '/');
  if (token === null || token.length < 32) return null;
  return { kind, token };
}

function messageReducer(
  state: ClientState,
  message: ServerMessage | LocalAction,
): ClientState {
  return reduce(state, message);
}

/**
 * The admin panel is a separate screen, decided before anything else.
 *
 * It does not open a socket, does not need an account in state, and must not
 * be reachable by accident from the game — so it is a path check at the very
 * top rather than a branch somewhere inside the lobby.
 */
export function App() {
  const path = location.pathname.replace(/\/+$/, '');
  if (path === '/admin') return <Admin session={localStorage.getItem(SESSION_KEY)} />;
  if (path === '/history') return <History session={localStorage.getItem(SESSION_KEY)} />;
  if (path === '/leaderboard') return <Leaderboard session={localStorage.getItem(SESSION_KEY)} />;
  if (path === '/profile') return <Profile session={localStorage.getItem(SESSION_KEY)} />;
  // No session needed: somebody who has not signed up yet is exactly who this
  // page is for.
  if (path === '/download') return <Download />;
  return <Room />;
}

function Room() {
  const [state, dispatch] = useReducer(messageReducer, initialState);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [authBusy, setAuthBusy] = useState(false);
  const [buyInSeat, setBuyInSeat] = useState<number | null>(null);
  const [link, setLink] = useState<ArrivalLink | null>(readArrivalLink);
  const [sound, setSound] = useState(soundEnabled);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [felt, setFelt] = useState(feltChoice);
  /**
   * Whether to show the sign-in form rather than the front page.
   *
   * True from the start when the browser already holds a session — somebody
   * coming back to a table does not want to be sold the room again — and when
   * the address is a page that only means something signed in.
   */
  const [wantsAuth, setWantsAuth] = useState(
    () => localStorage.getItem(SESSION_KEY) !== null || location.pathname !== '/',
  );
  const socketRef = useRef<GameSocket | null>(null);
  /** A verification link is spent the first time; a reconnect must not resend it. */
  const verifySent = useRef(false);

  useEffect(() => {
    const socket = new GameSocket({
      url: defaultSocketUrl(),
      session: localStorage.getItem(SESSION_KEY) ?? undefined,
      onMessage: (message) => {
        // Any answer to a credential attempt ends the pending state, whichever
        // way it went.
        if (message.type === 'session' || message.type === 'error' || message.type === 'notice') {
          setAuthBusy(false);
        }
        // The reset form has done its job once a session comes back.
        if (message.type === 'session') setLink(null);
        // Sound is driven from the events rather than from the snapshot: an
        // event is a thing that happened once, and a snapshot is a description
        // of now — playing from the latter means replaying the same noise every
        // time anything else changes.
        if (message.type === 'event') cue(message.event);
        dispatch(message);
      },
      onStatus: setStatus,
      onSession: (token) => localStorage.setItem(SESSION_KEY, token),
      onSessionRejected: () => localStorage.removeItem(SESSION_KEY),
    });
    socketRef.current = socket;
    return () => socket.close();
  }, []);

  // A verification link is acted on as soon as there is a socket to carry it.
  // It needs no account: the token is the proof, and the player may well be
  // signed out on the device where they opened their mail.
  useEffect(() => {
    if (link?.kind !== 'verify' || status !== 'open' || verifySent.current) return;
    verifySent.current = true;
    socketRef.current?.verifyEmail(link.token);
    setLink(null);
  }, [link, status]);

  /**
   * Walk into your own tournament when it starts.
   *
   * A Sit & Go begins the moment the last seat sells, which may be while the
   * player is looking at the lobby. Waiting for them to notice costs them
   * blinds, and blinds in a tournament are not refundable.
   */
  useEffect(() => {
    const mine = state.tournaments.find(
      (game) => game.youAreIn && game.status === 'running' && game.tableId !== null,
    );
    if (mine?.tableId == null) return;
    // Also when the table changes underneath them. A multi-table tournament
    // moves players to keep the field even, and somebody left watching the
    // table they were moved off would see a game they are no longer in.
    if (state.snapshot !== null && state.snapshot.tableId === mine.tableId) return;
    socketRef.current?.watch(mine.tableId);
  }, [state.tournaments, state.snapshot]);

  // The one sound that asks for something rather than reporting it, so it is
  // driven from the snapshot: it is not an event, it is a state you are in.
  const yourTurn =
    state.snapshot !== null &&
    state.snapshot.yourSeat !== null &&
    state.snapshot.actionOn === state.snapshot.yourSeat;
  const wasYourTurn = useRef(false);
  useEffect(() => {
    if (yourTurn && !wasYourTurn.current) play('turn');
    wasYourTurn.current = yourTurn;
  }, [yourTurn]);

  const act = useCallback(
    (action: PlayerAction) => {
      const snapshot = state.snapshot;
      if (snapshot !== null) socketRef.current?.act(snapshot.handNumber, action);
    },
    [state.snapshot],
  );

  if (state.account === null && link?.kind === 'reset') {
    return (
      <ResetPassword
        busy={authBusy}
        error={state.lastError?.message ?? null}
        onSubmit={(password) => {
          setAuthBusy(true);
          socketRef.current?.resetPassword(link.token, password);
        }}
        onCancel={() => setLink(null)}
      />
    );
  }

  if (state.account === null) {
    // A stranger gets the front page; the login is one click away. Somebody
    // who has asked to sign in — or who arrived on a page that needs an
    // account — gets the form straight away, because for them the front page
    // is the thing in the way.
    if (!wantsAuth) return <Landing onEnter={() => setWantsAuth(true)} />;
    return (
      <Auth
        busy={authBusy}
        error={state.lastError?.message ?? null}
        notice={state.lastNotice?.message ?? null}
        onLogin={(email, password) => {
          setAuthBusy(true);
          socketRef.current?.login(email, password);
        }}
        onRegister={(email, password, displayName) => {
          setAuthBusy(true);
          socketRef.current?.register(email, password, displayName);
        }}
        onForgot={(email) => {
          setAuthBusy(true);
          socketRef.current?.requestReset(email);
        }}
      />
    );
  }

  const account = state.account;

  if (state.snapshot === null) {
    return (
      <>
        <SiteHeader
          here="/"
          right={
            <div className="site-you">
              <div className="site-wallet">
                <span className="lat-ui">Chips</span>
                <b className="num lat-ui">{account.balance.toLocaleString('en-US')}</b>
              </div>
              {account.dailyGrantAvailable && (
                <button className="btn-raise small" onClick={() => socketRef.current?.claimDaily()}>
                  Claim Daily
                </button>
              )}
              <a className="site-avatar" href="/profile" title={account.displayName}>
                <Avatar name={account.displayName} />
              </a>
              <button
                className="btn-plain small"
                onClick={() => {
                  localStorage.removeItem(SESSION_KEY);
                  socketRef.current?.logout();
                  location.reload();
                }}
              >
                Sign Out
              </button>
            </div>
          }
        />
      <div className="page lobby">

        {!account.emailVerified && (
          <div className="verify-strip">
            <span>
              ایمیل <span className="lat">{account.email}</span> هنوز تأیید نشده. تا وقتی تأیید
              نشود، بازیابی رمز کار نمی‌کند.
            </span>
            <button className="btn-plain small" onClick={() => socketRef.current?.resendVerification()}>
              Send Link
            </button>
          </div>
        )}

        <PageTitle eyebrow={`خوش آمدید، ${account.displayName}`} title="The Tables">
          {status === 'open'
            ? 'یک میز انتخاب کنید یا در یک تورنمنت ثبت‌نام کنید. کارت‌ها تصادفی‌اند؛ تصمیم‌ها نه.'
            : 'در حال اتصال…'}
        </PageTitle>

        {state.tournaments.length > 0 && (
          <>
            <h2 className="section lat-ui">Sit &amp; Go</h2>
            <SngList
              tournaments={state.tournaments}
              walletBalance={account.balance}
              onRegister={(id) => socketRef.current?.registerSng(id)}
              onUnregister={(id) => socketRef.current?.unregisterSng(id)}
              onOpenTable={(tableId) => socketRef.current?.watch(tableId)}
            />
          </>
        )}

        <h2 className="section lat-ui">Cash Games</h2>
        <div className="table-list">
          {state.tables.map((table) => (
            <button
              key={table.tableId}
              className="table-row"
              onClick={() => socketRef.current?.watch(table.tableId)}
            >
              <span className="t-name">{table.name}</span>
              <span className="t-meta num lat-ui">
                {table.smallBlind}/{table.bigBlind}
              </span>
              <span className="t-seats num lat-ui">
                {table.seated}/{table.maxSeats} seated
              </span>
            </button>
          ))}
        </div>

        {state.lastNotice !== null && (
          <Toast key={state.lastNotice.message} text={state.lastNotice.message} kind="good" />
        )}
        {state.lastError !== null && (
          <Toast key={state.lastError.message} text={state.lastError.message} />
        )}
      </div>
      </>
    );
  }

  const snapshot = state.snapshot;

  return (
    <div className="game">
      <div className="topbar">
        <span className="t-title">{snapshot.name}</span>
        <span className="t-blinds num lat-ui">
          {snapshot.smallBlind}/{snapshot.bigBlind} · Hand #{snapshot.handNumber}
        </span>
        <span className="spacer" />
        <span className="wallet lat-ui" title="Wallet">
          {account.balance.toLocaleString('en-US')}
        </span>
        <button
          className="home-link"
          title="Home"
          aria-label="Home"
          onClick={() => {
            if (snapshot.yourSeat !== null && snapshot.tournament === null) {
              socketRef.current?.stand();
            }
            socketRef.current?.leaveTableView();
            dispatch({ type: 'left-table' });
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M3 11.2 12 4l9 7.2V20a1 1 0 0 1-1 1h-5.5v-6h-5v6H4a1 1 0 0 1-1-1z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          </svg>
          <span>Lobby</span>
        </button>
        <button
          className="btn-plain small"
          title="Hand History"
          onClick={() => setHistoryOpen(true)}
        >
          Hands
        </button>
        {/* One button rather than three: there are only three tables, and a
            player who wants a different one wants the next one. */}
        <button
          className={`felt-toggle ${felt}`}
          title={`Table: ${FELT_LABEL[felt]}`}
          aria-label={`Table: ${FELT_LABEL[felt]}`}
          onClick={() => {
            const next = nextFelt(felt);
            setFelt(next);
            setFeltChoice(next);
          }}
        />
        <button
          className={`sound-toggle${sound ? ' on' : ''}`}
          title={sound ? 'صدا روشن' : 'صدا خاموش'}
          aria-label={sound ? 'صدا روشن' : 'صدا خاموش'}
          onClick={() => {
            const next = !sound;
            setSound(next);
            setSoundEnabled(next);
            // Play the moment it is switched on, both as confirmation and
            // because a browser will not start audio without a gesture and
            // this is the gesture.
            if (next) play('call');
          }}
        >
          {sound ? '🔊' : '🔇'}
        </button>
        <span className={`conn${status !== 'open' ? ' bad' : ''}`}>
          {status === 'open' ? 'Connected' : 'در حال اتصال مجدد…'}
        </span>
      </div>

      {snapshot.tournament !== null && <SngBar view={snapshot.tournament} />}

      <div className="stage-row">
        <Table
          snapshot={snapshot}
          lastActions={state.lastActions}
          felt={felt}
          onSit={(seat) => setBuyInSeat(seat)}
        />
        <div className="feed-panel">
          <div className="feed-head lat-ui">Action</div>
          <div className="feed">
            {state.feed.map((item) => (
              <div key={item.id} className={item.strong ? 'strong' : 'quiet'}>
                {item.text}
              </div>
            ))}
          </div>
          {snapshot.tournament !== null && <SngStandings view={snapshot.tournament} />}
        </div>
      </div>

      <Actions
        snapshot={snapshot}
        onAct={act}
        onStand={() => {
          // Three cases, one button. Standing up from a cash table takes your
          // chips with you. In a tournament there is nothing to take — the
          // chips are not yours — so leaving means going back to the lobby
          // while your stack keeps posting blinds. And a spectator has no seat
          // to stand up from, so asking to would earn them an error message
          // for pressing the only control on the screen.
          if (snapshot.yourSeat !== null && snapshot.tournament === null) {
            socketRef.current?.stand();
          }
          socketRef.current?.leaveTableView();
          dispatch({ type: 'left-table' });
        }}
        onSittingOut={(out) => socketRef.current?.setSittingOut(out)}
        onRebuy={(amount) => socketRef.current?.rebuy(amount)}
      />

      {buyInSeat !== null && (
        <BuyInDialog
          seat={buyInSeat}
          min={snapshot.minBuyIn}
          max={Math.min(snapshot.maxBuyIn, account.balance)}
          hardMax={snapshot.maxBuyIn}
          bigBlind={snapshot.bigBlind}
          walletBalance={account.balance}
          onCancel={() => setBuyInSeat(null)}
          onConfirm={(amount) => {
            socketRef.current?.sit(buyInSeat, amount);
            setBuyInSeat(null);
          }}
        />
      )}

      {historyOpen && (
        <div className="drawer-scrim" onClick={() => setHistoryOpen(false)}>
          {/* The drawer itself swallows the click, so only the felt around it
              closes the thing — a player scrolling their own hands must not
              lose the panel every time they touch it. */}
          <div className="drawer" onClick={(event) => event.stopPropagation()}>
            <div className="drawer-head">
              <span className="lat-ui">Hand History</span>
              <span className="spacer" />
              <button className="btn-plain small" onClick={() => setHistoryOpen(false)}>
                Close
              </button>
            </div>
            <History session={localStorage.getItem(SESSION_KEY)} embedded />
          </div>
        </div>
      )}

      {state.lastError !== null && <Toast key={state.lastError.message} text={state.lastError.message} />}
    </div>
  );
}

function BuyInDialog({
  seat,
  min,
  max,
  hardMax,
  bigBlind,
  walletBalance,
  onCancel,
  onConfirm,
}: {
  seat: number;
  min: number;
  max: number;
  hardMax: number;
  bigBlind: number;
  walletBalance: number;
  onCancel: () => void;
  onConfirm: (amount: number) => void;
}) {
  const [amount, setAmount] = useState(max);
  const tooPoor = walletBalance < min;

  return (
    <div className="overlay" onClick={onCancel}>
      <div className="dialog" onClick={(event) => event.stopPropagation()}>
        <h2>Seat {seat + 1}</h2>
        <p className="hint">
          Buy-in {bb(min, bigBlind)} — {bb(hardMax, bigBlind)}
        </p>

        {tooPoor ? (
          <>
            <p className="hint">
              کیف پول شما {walletBalance.toLocaleString('en-US')} چیپ دارد و حداقل بای-این این میز{' '}
              {min.toLocaleString('en-US')} است.
            </p>
            <div className="buttons">
              <button className="btn-plain" onClick={onCancel}>
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <input
              type="range"
              min={min}
              max={max}
              value={amount}
              onChange={(event) => setAmount(Number(event.target.value))}
              style={{ direction: 'ltr', accentColor: 'var(--brass)' }}
            />
            <div
              className="num lat-ui"
              style={{ textAlign: 'center', fontSize: 24, fontWeight: 800, color: 'var(--brass)' }}
            >
              {bb(amount, bigBlind)}
            </div>
            {max < hardMax && (
              <p className="hint">سقف بای-این شما به موجودی کیف پول محدود شده است.</p>
            )}
            <div className="buttons">
              <button className="btn-plain" onClick={onCancel}>
                Cancel
              </button>
              <button className="btn-check" onClick={() => onConfirm(amount)}>
                Sit Down
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** A short-lived toast; the key on the caller remounts it per message. */
function Toast({ text, kind = 'bad' }: { text: string; kind?: 'good' | 'bad' }) {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setVisible(false), kind === 'good' ? 6_000 : 4_000);
    return () => clearTimeout(timer);
  }, [kind]);
  if (!visible) return null;
  return <div className={`toast${kind === 'good' ? ' good' : ''}`}>{text}</div>;
}
